import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Phone, Clock, Truck, ArrowLeft, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load laundry services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  const getServiceTypeDisplay = (serviceType) => {
    const types = {
      'wash_and_fold': 'Wash & Fold',
      'dry_clean': 'Dry Clean',
      'ironing': 'Ironing',
      'stain_removal': 'Stain Removal'
    };
    return types[serviceType] || serviceType;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading laundry services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-teal-600 hover:text-teal-700 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Available Laundry Services</h1>
              <p className="text-gray-600">Choose from trusted local vendors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Vendors Grid */}
      <div className="container mx-auto px-4 py-8">
        {vendors.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No laundry services available at the moment.</p>
            <Link to="/" className="btn-primary mt-4">
              Go Back Home
            </Link>
          </div>
        ) : (
          <div className="vendors-grid">
            {vendors.map((vendor) => (
              <div key={vendor.id} className="vendor-card">
                <div className="vendor-header">
                  <h3 className="vendor-name">{vendor.name}</h3>
                  <div className="vendor-rating">
                    <div className="flex items-center gap-1">
                      {renderStars(vendor.rating)}
                    </div>
                    <span className="ml-2 font-semibold">{vendor.rating}</span>
                  </div>
                </div>
                
                <div className="vendor-body">
                  <p className="vendor-description">{vendor.description}</p>
                  
                  <div className="vendor-info">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.address}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <Phone size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} className="text-teal-600" />
                      <span className="text-sm">{vendor.operating_hours}</span>
                    </div>
                    {vendor.website && (
                      <div className="flex items-center gap-2 mb-2">
                        <Globe size={16} className="text-teal-600" />
                        <a 
                          href={vendor.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-teal-600 hover:text-teal-700 underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                    {(vendor.pickup_available || vendor.delivery_available) && (
                      <div className="flex items-center gap-2">
                        <Truck size={16} className="text-teal-600" />
                        <span className="text-sm">
                          {vendor.pickup_available && vendor.delivery_available
                            ? 'Pickup & Delivery Available'
                            : vendor.pickup_available
                            ? 'Pickup Available'
                            : 'Delivery Available'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="services-list">
                    {vendor.services?.map((service) => (
                      <span key={service.id} className="service-tag">
                        {getServiceTypeDisplay(service.type)} - ${service.price_per_lb}/lb
                      </span>
                    ))}
                  </div>

                  <Link
                    to={`/booking/${vendor.id}`}
                    className="btn-primary w-full text-center block"
                  >
                    Book Service
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default VendorsPage;